import { Link, useParams } from "react-router-dom";
import { OFFERS } from "../data/offers";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { CheckoutButton } from "../components/CheckoutButton";

export function OfferPage() {
  const { slug } = useParams();
  const offer = slug ? OFFERS[slug] : undefined;
  useDocumentTitle(offer ? `${offer.name} — Pilot Mastery Academy` : "Program — Pilot Mastery Academy");

  if (!offer) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-20">
        <h1 className="font-display text-2xl text-white">Program not found</h1>
        <Link to="/" className="mt-4 inline-block text-signal-sky">
          ← Home
        </Link>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
      <nav className="text-sm text-slate-500">
        <Link to="/" className="hover:text-signal-sky">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-slate-400">Offers</span>
        <span className="mx-2">/</span>
        <span className="text-slate-300">{offer.name}</span>
      </nav>

      <div className="mt-8 overflow-hidden rounded-xl">
        <img
          src={`/images/offers/${offer.slug}.jpg`}
          alt={offer.name}
          className="h-64 w-full object-cover sm:h-80"
        />
      </div>

      <header className="mt-8 max-w-3xl">
        <p className="text-sm font-medium uppercase tracking-wider text-signal-sky">Offer #{offer.id}</p>
        <h1 className="mt-2 font-display text-4xl font-semibold text-white">{offer.name}</h1>
        <p className="mt-4 text-xl text-slate-300">{offer.headline}</p>
        <p className="mt-4 text-slate-400">{offer.subhead}</p>
        <div className="mt-8 flex flex-wrap items-baseline gap-4 border-y border-white/10 py-6">
          <span className="font-display text-3xl font-semibold text-signal-amber">{offer.price}</span>
          <span className="text-sm text-slate-500">{offer.priceNote}</span>
        </div>
      </header>

      <div className="mt-12 grid gap-12 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-12">
          <section>
            <h2 className="font-display text-2xl font-semibold text-white">Who it is for</h2>
            <p className="mt-4 text-slate-300">{offer.promise}</p>
            <h3 className="mt-8 text-sm font-semibold uppercase tracking-wider text-slate-500">Common pains</h3>
            <ul className="mt-3 list-inside list-disc space-y-2 text-slate-400">
              {offer.problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
            <h3 className="mt-8 text-sm font-semibold uppercase tracking-wider text-slate-500">Outcomes (positioning)</h3>
            <ul className="mt-3 list-inside list-disc space-y-2 text-slate-400">
              {offer.outcomes.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold text-white">Curriculum overview</h2>
            <ol className="mt-6 space-y-4">
              {offer.modules.map((m, i) => (
                <li
                  key={m.title}
                  className="flex gap-4 rounded-xl border border-white/10 bg-deck-panel/40 p-4"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-deck-deep font-mono text-sm text-signal-amber">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-medium text-white">{m.title}</h3>
                    <p className="mt-1 text-sm text-slate-400">{m.summary}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-8 space-y-6 rounded-2xl border border-signal-amber/30 bg-deck-deep/80 p-6 shadow-lift">
            <p className="text-sm font-medium text-signal-amber">Next step</p>
            <p className="text-sm text-slate-300">{offer.cta}</p>
            <p className="text-xs text-slate-500">
              Stripe Checkout (server): metadata <code className="text-signal-sky">offer_key</code> maps to PRD offer #{offer.id}.
            </p>
            <CheckoutButton offerKey={offer.checkoutKey}>Pay securely with Stripe</CheckoutButton>
            <a
              href="mailto:hello@pilotmastery.academy?subject=Pilot%20Mastery%20Academy%20—%20Offer%20"
              className="mt-3 block w-full rounded-lg border border-white/20 py-3 text-center text-sm font-semibold text-slate-200 hover:border-signal-sky hover:text-signal-sky"
            >
              Or email us
            </a>
            <div className="border-t border-white/10 pt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Compliance</p>
              <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-slate-500">
                {offer.compliance.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}
